/**
 * TanStack Query hooks for the F&O Performance Console (`/fo/console/*`).
 *
 * Two things distinguish these from the rest of `hooks/fo.ts`:
 *
 * 1. **Every per-user call carries a `reason`** (D7, 2026-08-21). There is no
 *    investigator role and no identity store in NOVA; the backend gates the
 *    per-user routes on a config allow-list of sign-in mails (403 otherwise),
 *    audit-logs who looked at whom and why, and refuses (400) a call without a
 *    reason of at least four characters. The hooks therefore take the reason as
 *    an argument and are `enabled` only when one is present — a per-user query
 *    never fires speculatively. The reason itself lives in `sessionStorage`
 *    (`features/dynamics/reason-gate.tsx`), not here.
 * 2. **Identity-bearing answers are never persisted.** The user view, the record,
 *    the search candidates and the audit list are excluded in
 *    `lib/api/persist.ts` and additionally carry a short `gcTime`, so a person's
 *    timeline does not outlive the investigation it was opened for.
 *
 * The poll interval follows the payload's `cache_ttl_sec`, as in `hooks/fo.ts`.
 * Per-user views are computed on demand in the workspace, so they do not poll
 * at all — a refetch is a new audit-log line, and an idle tab must not write
 * one every minute.
 *
 * SDK functions and types come from the generated client, like every other hook
 * file. Two unions the generator inlines rather than names are given names here
 * (`FoConsoleLookback`, `FoConsoleCollectTaskName`) so the feature can spell
 * them; they are derived from the generated types, never re-typed.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  type FoConsoleReportIn,
  type FoConsoleReportList,
  type FoConsoleReportSummary,
  type FoConsoleRumSummary,
  type FoConsoleSlowSummary,
  getFoConsoleAudit,
  getFoConsoleCollectStatus,
  getFoConsoleDevice,
  getFoConsoleExperience,
  getFoConsoleFinding,
  getFoConsolePinpoint,
  getFoConsolePlatform,
  getFoConsoleRecord,
  getFoConsoleReport,
  getFoConsoleRumStatus,
  getFoConsoleSlowSummary,
  getFoConsoleUser,
  listFoConsoleReports,
  runFoConsoleCollect,
  type RunFoConsoleCollectData,
  searchFoConsoleUsers,
  updateFoConsoleReport,
} from "@/lib/api/generated";
import { logger } from "@/lib/logger";

export type {
  FoConsoleAvailabilityLocation,
  FoConsoleBatchJob,
  FoConsoleBatchLoad,
  FoConsoleCompanyLoad,
  FoConsoleContrastHour,
  FoConsoleDevice,
  FoConsoleDeviceFacts,
  FoConsoleExperience,
  FoConsoleExperienceApp,
  FoConsoleExperienceContext,
  FoConsoleExperienceHour,
  FoConsoleExperienceProbe,
  FoConsoleFindingConcentration,
  FoConsoleFindingDetail,
  FoConsoleFindingDistribution,
  FoConsoleFindingHour,
  FoConsoleFindingPeer,
  FoConsoleFindingTrendPoint,
  FoConsolePinpoint,
  FoConsolePinpointCoverage,
  FoConsolePinpointEvidence,
  FoConsolePinpointFinding,
  FoConsolePlatform,
  FoConsoleReportDetail,
  FoConsoleReportList,
  FoConsoleReportMatchRow,
  FoConsoleReportSummary,
  FoConsoleRumAppStatus,
} from "@/lib/api/generated";

/** The lookback windows `FoSlowInteractions(lookback)` is served for — the payload's own union. */
export type FoConsoleLookback = FoConsoleSlowSummary["lookback"];

/* ------------------------------------------------------------------------ *
 * Unions the generator inlines rather than names
 * ------------------------------------------------------------------------ */

/**
 * The apps extension v0.2 reports for, as the intake body spells them.
 *
 * The aggregate payloads carry `app` as a plain string on purpose — a board
 * must render a label the workspace returned even if this union has not caught
 * up — so this type names the contract, it does not police live data.
 */
export type FoConsoleRumApp = FoConsoleReportIn["app"];

/**
 * How function v4 joined a browser minute onto a slow row.
 *
 * The payload carries it as a string; `rumJoinKind` narrows an unknown label to
 * `bucket` rather than dropping the block.
 */
export type FoConsoleRumJoinKind = "activity" | "bucket" | "none";

/** A user report's triage state. */
export type FoConsoleReportStatus = FoConsoleReportSummary["status"];

/** The report store's own windows — a Postgres filter, never a KQL lookback. */
export type FoConsoleReportLookback = FoConsoleReportList["lookback"];

/**
 * A record's browser block.
 *
 * Schema v2 landed on `FoConsoleRumSummary` itself, so this is a plain alias
 * kept for the name the console reads by. Every v2 field is nullable: a minute
 * written by extension v0.1, or read through a v3 function, has none of them,
 * and the drawer renders that as "not measured", never as a zero.
 */
export type FoConsoleRumBlockV2 = FoConsoleRumSummary;

/* ------------------------------------------------------------------------ *
 * Browser RUM status — contract v3
 * ------------------------------------------------------------------------ */

export const FO_CONSOLE_RUM_STATUS_KEY = ["fo", "console", "rum-status"] as const;

/**
 * Whether the fo-rum extension reports, and for what share of active F&O
 * users (`/fo/console/rum/status`). An aggregate — counts, a ratio, a
 * timestamp, no poster identity — so it polls like the collector strip and is
 * persistable.
 */
export function useFoConsoleRumStatus() {
  return useQuery({
    queryKey: FO_CONSOLE_RUM_STATUS_KEY,
    queryFn: async () => {
      const { data } = await getFoConsoleRumStatus();
      logger.debug("[fo-console] rum status received", {
        configured: data?.configured,
        enabled: data?.enabled,
        users24h: data?.users_24h,
        coveragePct: data?.coverage?.pct,
      });
      return data;
    },
    refetchInterval: 60_000,
    placeholderData: (previous) => previous,
  });
}

/** The collector tasks "Run now" may trigger — the route's own union. */
export type FoConsoleCollectTaskName = RunFoConsoleCollectData["query"]["task"];

/** Root key shared by the console's queries — `["fo", "console", …]`. */
export const FO_CONSOLE_KEY = ["fo", "console"] as const;
export const FO_CONSOLE_SLOW_SUMMARY_KEY = [...FO_CONSOLE_KEY, "slow-summary"] as const;
export const FO_CONSOLE_SEARCH_KEY = [...FO_CONSOLE_KEY, "search"] as const;
export const FO_CONSOLE_USER_KEY = [...FO_CONSOLE_KEY, "user"] as const;
export const FO_CONSOLE_RECORD_KEY = [...FO_CONSOLE_KEY, "record"] as const;
export const FO_CONSOLE_AUDIT_KEY = [...FO_CONSOLE_KEY, "audit"] as const;
export const FO_CONSOLE_COLLECT_KEY = [...FO_CONSOLE_KEY, "collect-status"] as const;
export const FO_CONSOLE_DEVICE_KEY = [...FO_CONSOLE_KEY, "device"] as const;
export const FO_CONSOLE_PINPOINT_KEY = [...FO_CONSOLE_KEY, "pinpoint"] as const;
/** One finding's drawer, keyed by the cell it is about: form, company, lookback. */
export const FO_CONSOLE_FINDING_KEY = [...FO_CONSOLE_KEY, "finding"] as const;
/** The platform panel: batch jobs and the synthetic front-door tests. */
const FO_CONSOLE_PLATFORM_KEY = ["fo-console", "platform"] as const;

/** Before the first response there is no TTL to read; one conservative interval. */
const FALLBACK_INTERVAL_MS = 120_000;

/**
 * How long identity-bearing answers stay in memory once nothing renders them.
 * Two minutes: long enough to walk drawer → grid → drawer without a second
 * audit line, short enough that a closed investigation is gone.
 */
const IDENTITY_GC_MS = 2 * 60_000;

function intervalFromTtl(data: { cache_ttl_sec: number } | undefined): number {
  const ttlSec = data?.cache_ttl_sec;
  return ttlSec != null && ttlSec > 0 ? ttlSec * 1000 : FALLBACK_INTERVAL_MS;
}

/** Aggregates of the slow-interaction records over `lookback` (no identity). */
export function useFoConsoleSlowSummary(lookback: FoConsoleLookback) {
  return useQuery({
    queryKey: [...FO_CONSOLE_SLOW_SUMMARY_KEY, lookback],
    queryFn: async () => {
      const { data } = await getFoConsoleSlowSummary({ query: { lookback } });
      logger.debug("[fo-console] slow summary received", {
        accessState: data?.access_state,
        lookback: data?.lookback,
        total: data?.total,
        coveragePct: data?.coverage?.pct,
      });
      return data;
    },
    refetchInterval: (query) => intervalFromTtl(query.state.data),
    placeholderData: (previous) => previous,
  });
}

/**
 * What to fix, in what order, and what it costs (`/fo/console/pinpoint`).
 *
 * Ranked findings, the per-company load table and the browser-telemetry
 * coverage. No identities: forms, legal-entity codes and counts only — but it
 * sits behind the same investigator gate as the per-person screens, because a
 * ranked list of which company is worst is an operational judgement about named
 * parts of the business. So a 403 is a definite answer and `retry: false`;
 * unlike the per-person routes it carries **no reason** and writes no audit row,
 * so it may poll on the payload's TTL like the other aggregates.
 *
 * Not in `PERSISTABLE_KEYS` (`lib/api/persist.ts`), which is an allow-list:
 * nothing is written to sessionStorage until someone deliberately lists it, and
 * a gated per-company ranking is not a thing to leave on a shared machine's
 * disk for the sake of one saved round-trip.
 */
export function useFoConsolePinpoint(lookback: FoConsoleLookback) {
  return useQuery({
    queryKey: [...FO_CONSOLE_PINPOINT_KEY, lookback],
    queryFn: async () => {
      const { data } = await getFoConsolePinpoint({ query: { lookback } });
      logger.debug("[fo-console] pinpoint received", {
        accessState: data?.access_state,
        lookback: data?.lookback,
        findings: data?.findings.length ?? 0,
        companies: data?.companies.length ?? 0,
        coverageMeasured: data?.coverage != null,
      });
      return data;
    },
    refetchInterval: (query) => intervalFromTtl(query.state.data),
    placeholderData: (previous) => previous,
    retry: false,
  });
}

/**
 * What the platform was doing while people used it — batch jobs and the front door.
 *
 * Cached longer than the findings because it moves slowly: a batch retry loop
 * runs for days and the synthetic tests are five minutes apart.
 *
 * Not in `PERSISTABLE_KEYS` for the same reason as the findings panel: it names
 * batch jobs and companies, which is an operational picture of the business
 * rather than a board figure, and it should not sit on a shared machine's disk
 * to save a round-trip.
 */
export function useFoConsolePlatform(lookback: FoConsoleLookback) {
  return useQuery({
    queryKey: [...FO_CONSOLE_PLATFORM_KEY, lookback],
    queryFn: async () => {
      const { data } = await getFoConsolePlatform({ query: { lookback } });
      logger.debug("[fo-console] platform received", {
        accessState: data?.access_state,
        lookback: data?.lookback,
        batchJobs: data?.batch?.jobs_listed?.length ?? 0,
        availability: data?.availability.length ?? 0,
        contrast: data?.contrast.length ?? 0,
      });
      return data;
    },
    refetchInterval: (query) => intervalFromTtl(query.state.data),
    placeholderData: (previous) => previous,
    retry: false,
  });
}

/**
 * One board row opened up (`/fo/console/finding`) — the drawer behind a finding.
 *
 * Four measurements a ranked row cannot carry: the cell's own five percentiles,
 * the same five pooled across every **other** company, a dot per company, and
 * the median by hour of day. The p10 is what the panel exists for — a form
 * whose fastest tenth still costs half a minute is a different ticket from one
 * that is quick for most people and catastrophic for a few, and a median cannot
 * tell them apart.
 *
 * **On demand, and only then.** Four queries per row across a 25-row board is a
 * hundred queries for a drawer nobody opened, which is precisely why this is
 * not part of `/pinpoint`. So the hook is `enabled` only once a finding is
 * actually open: pass the open row's own `form` and `company`, and `null` (or
 * the empty string a `form_all` finding carries) while nothing is open. Empty
 * is also the honest disabled state rather than a request — the route takes one
 * legal-entity code and answers 400 for anything else, "all of them" included.
 *
 * Forms, company codes and counts — no identity, no reason, no audit row — so
 * it may poll on the payload's TTL like the board it drills into, and does so
 * only while the drawer is open. `retry: false`: a 403 from outside the
 * investigator list and a 400 for a shape this estate does not produce are both
 * final answers, not blips.
 *
 * Deliberately **not** in `PERSISTABLE_KEYS` (`lib/api/persist.ts`), for the
 * same reason as the board above it: that list is an allow-list, and one
 * company's measured disadvantage against its peers is not a thing to leave on
 * a shared machine's disk to save a round-trip.
 */
export function useFoConsoleFinding(
  form: string | null,
  company: string | null,
  lookback: FoConsoleLookback,
) {
  return useQuery({
    queryKey: [...FO_CONSOLE_FINDING_KEY, form ?? "", company ?? "", lookback],
    queryFn: async () => {
      const { data } = await getFoConsoleFinding({
        query: { form: form as string, company: company as string, lookback },
      });
      logger.debug("[fo-console] finding received", {
        accessState: data?.access_state,
        form: data?.form,
        company: data?.company,
        lookback: data?.lookback,
        distribution: data?.distribution != null,
        peers: data?.peers.length ?? 0,
        hours: data?.hourly.length ?? 0,
      });
      return data;
    },
    enabled: Boolean(form) && Boolean(company),
    refetchInterval: (query) => intervalFromTtl(query.state.data),
    placeholderData: (previous) => previous,
    retry: false,
  });
}

/**
 * People matching `q` by display name, mail or F&O alias (≤ 10). Disabled
 * below two characters so a single keystroke never fans out a directory read.
 */
export function useFoConsoleUserSearch(q: string) {
  const trimmed = q.trim();
  return useQuery({
    queryKey: [...FO_CONSOLE_SEARCH_KEY, trimmed],
    queryFn: async () => {
      const { data } = await searchFoConsoleUsers({ query: { q: trimmed } });
      logger.debug("[fo-console] search answered", { q: trimmed, candidates: data?.length ?? 0 });
      return data ?? [];
    },
    enabled: trimmed.length >= 2,
    gcTime: IDENTITY_GC_MS,
    staleTime: 30_000,
    placeholderData: (previous) => previous,
  });
}

/**
 * One person's lookback: page views, exceptions, network buckets, slow rows.
 *
 * Fires only with a mail AND a reason; the backend logs every call. No polling.
 */
export function useFoConsoleUser(
  mail: string | undefined,
  lookback: FoConsoleLookback,
  reason: string | null,
) {
  return useQuery({
    // The reason is part of the key on purpose: a second ticket is a second reason,
    // and a second reason must be a second, separately audited call (D7).
    queryKey: [...FO_CONSOLE_USER_KEY, mail ?? "", lookback, reason ?? ""],
    queryFn: async () => {
      const { data } = await getFoConsoleUser({
        query: { mail: mail as string, lookback, reason: reason as string },
      });
      logger.debug("[fo-console] user view received", {
        accessState: data?.access_state,
        pageViews: data?.timeline.length ?? 0,
        slow: data?.slow.length ?? 0,
        networkBuckets: data?.network.length ?? 0,
      });
      return data;
    },
    enabled: Boolean(mail) && Boolean(reason),
    gcTime: IDENTITY_GC_MS,
    staleTime: 60_000,
    // A 400/404 here is a definite answer, not a blip — retrying re-logs it.
    retry: false,
  });
}

/** One §5 record by `activity_id`, with the next-diagnostic actions. Audit-logged. */
export function useFoConsoleRecord(activityId: string | null, reason: string | null) {
  return useQuery({
    queryKey: [...FO_CONSOLE_RECORD_KEY, activityId ?? "", reason ?? ""],
    queryFn: async () => {
      const { data } = await getFoConsoleRecord({
        query: { activity_id: activityId as string, reason: reason as string },
      });
      logger.debug("[fo-console] record received", {
        accessState: data?.access_state,
        found: data?.row != null,
      });
      return data;
    },
    enabled: Boolean(activityId) && Boolean(reason),
    gcTime: IDENTITY_GC_MS,
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * The machines Defender has seen this person signed in on (`/fo/console/device`).
 *
 * Static asset facts — model, vendor, OS build, join type, onboarding status,
 * last seen — from one Defender advanced-hunting query over `DeviceInfo`. It is
 * the half `BrowserRUM_CL` cannot answer: the browser can say what the machine
 * was *doing*, never what it *is*.
 *
 * Gated and audited exactly like `/user` and `/record`, so it obeys the same
 * three rules: it fires only with a mail **and** a reason, the reason is part of
 * the key (a second ticket is a second reason, and a second reason must be a
 * second, separately audited call — D7), and it never polls, because a refetch
 * is another audit row and a five-minute-old model number has not moved.
 *
 * `retry: false` for the same reason as the other per-user hooks *and* one more:
 * `ThreatHunting.Read.All` is not consented on NOVA's Graph credential today,
 * so the honest first answer in production is a 200 carrying
 * `access_state: "unauthorised"`. That is a state to render, not a blip to
 * retry — and the states that do arrive as errors (a 403 off the investigator
 * list, a 400 for a short reason, a 404 for an unknown mail) are equally final.
 *
 * Lazy by design: the caller passes `undefined` for the mail while its section
 * is collapsed, so the audited call costs nothing until someone opens it.
 */
export function useFoConsoleDevice(mail: string | undefined, reason: string | null) {
  return useQuery({
    queryKey: [...FO_CONSOLE_DEVICE_KEY, mail ?? "", reason ?? ""],
    queryFn: async () => {
      const { data } = await getFoConsoleDevice({
        query: { mail: mail as string, reason: reason as string },
      });
      logger.debug("[fo-console] device facts received", {
        configured: data?.configured,
        accessState: data?.access_state,
        devices: data?.devices.length ?? 0,
      });
      return data;
    },
    enabled: Boolean(mail) && Boolean(reason),
    gcTime: IDENTITY_GC_MS,
    staleTime: 60_000,
    retry: false,
  });
}

/** Who looked at whom — the audit trail the per-user views write. Lazy by design. */
export function useFoConsoleAudit(limit: number, enabled: boolean) {
  return useQuery({
    queryKey: [...FO_CONSOLE_AUDIT_KEY, limit],
    queryFn: async () => {
      const { data } = await getFoConsoleAudit({ query: { limit } });
      return data;
    },
    enabled,
    gcTime: IDENTITY_GC_MS,
  });
}

/** The two collector tasks (DEM every 15 min, identity daily) and their last run. */
export function useFoConsoleCollectStatus() {
  return useQuery({
    queryKey: FO_CONSOLE_COLLECT_KEY,
    queryFn: async () => {
      const { data } = await getFoConsoleCollectStatus();
      logger.debug("[fo-console] collect status received", {
        tasks: data?.tasks.map((task) => `${task.name}:${task.error ? "error" : "ok"}`),
      });
      return data;
    },
    refetchInterval: 60_000,
  });
}

/** Run one collector task now. The response is the new status, so it seeds the cache. */
export function useRunFoConsoleCollect() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (task: FoConsoleCollectTaskName) => {
      const { data } = await runFoConsoleCollect({ query: { task } });
      return data;
    },
    onSuccess: (data, task) => {
      logger.debug("[fo-console] collect run finished", { task, triggered: data?.triggered });
      if (data) {
        queryClient.setQueryData(FO_CONSOLE_COLLECT_KEY, data);
      }
      // A fresh DEM window changes the slow-summary coverage; re-read it.
      void queryClient.invalidateQueries({ queryKey: FO_CONSOLE_SLOW_SUMMARY_KEY });
    },
  });
}

/* ------------------------------------------------------------------------ *
 * Contract v2 — reports and the cross-app experience panel
 * ------------------------------------------------------------------------ */

export const FO_CONSOLE_REPORTS_KEY = [...FO_CONSOLE_KEY, "reports"] as const;
export const FO_CONSOLE_REPORT_KEY = [...FO_CONSOLE_KEY, "report"] as const;
export const FO_CONSOLE_EXPERIENCE_KEY = [...FO_CONSOLE_KEY, "experience"] as const;

/**
 * The investigator list of user-submitted reports. Needs no reason — the list
 * is the queue, and a report is the submitter's own data offered on purpose —
 * but every row still names a person, so it is excluded from the persisted
 * cache and leaves memory two minutes after the last panel drops it.
 *
 * The window is the report store's own union (`24h|7d|30d|90d`, a Postgres
 * filter), **not** the console's KQL lookback: the queue outlives the five
 * trailing windows `FoSlowInteractions()` is served for.
 *
 * No polling, and no retry: a queue refreshes when someone acts on it (the
 * status mutation invalidates this key) or when the window changes, and a 403
 * from outside the investigator list is an answer, not a blip to retry.
 */
export function useFoConsoleReports(
  lookback: FoConsoleReportLookback,
  status: FoConsoleReportStatus | null,
  enabled = true,
) {
  return useQuery({
    queryKey: [...FO_CONSOLE_REPORTS_KEY, lookback, status ?? "all"],
    queryFn: async () => {
      const { data } = await listFoConsoleReports({
        query: { lookback, ...(status ? { status } : {}) },
      });
      logger.debug("[fo-console] reports received", {
        available: data?.available,
        count: data?.reports.length ?? 0,
        lookback,
        status,
      });
      return data;
    },
    enabled,
    gcTime: IDENTITY_GC_MS,
    staleTime: 60_000,
    retry: false,
    placeholderData: (previous) => previous,
  });
}

/* ------------------------------------------------------------------------ *
 * The investigator gate, derived from the API
 * ------------------------------------------------------------------------ */

/**
 * Whether this sign-in is on `NOVA_FO_INVESTIGATORS`, as far as the API has said.
 *
 * Three values, never two: `unknown` is the honest state before the first
 * answer lands, and callers must not collapse it into `denied` (see
 * `components/nav-access.ts` for what the nav does with each).
 */
export type FoInvestigatorAccess = "granted" | "denied" | "unknown";

/**
 * Ask the API whether the reader may open the per-person screens.
 *
 * **Why the reports list is the probe.** The gate is a backend allow-list of
 * sign-in mails; nothing in `/auth/me` reflects it, and the frontend must never
 * hold a copy of the list. `GET /fo/console/reports` is the one gated route
 * that needs no reason and writes no audit row (a report is its submitter's own
 * data, offered on purpose), so a 403 from it is a clean read of the gate — no
 * per-user telemetry is touched to find out.
 *
 * It shares its query key with the queue itself and with the overview's "Open
 * reports" tile, so on `/dynamics`, `/dynamics/reports` and `/overview` this
 * costs no extra request at all; elsewhere it is one cached Postgres read.
 * `openReports` comes back with it because the nav wants the queue length
 * beside the row and that answer is already in the payload.
 */
export function useFoInvestigatorAccess(): {
  access: FoInvestigatorAccess;
  /** New (untriaged) reports in the default window, or `null` when unknown. */
  openReports: number | null;
} {
  const reports = useFoConsoleReports(GATE_PROBE_LOOKBACK, null);
  const status = thrownStatus(reports.error);

  if (status === 403) {
    return { access: "denied", openReports: null };
  }
  if (reports.data != null) {
    // An answered call proves the gate opened, whatever the store then said.
    // `available: false` is the store failing, not the gate — so the count is
    // unknown while the access stays granted.
    return {
      access: "granted",
      openReports: reports.data.available ? (reports.data.counts.new ?? 0) : null,
    };
  }
  // Any other error (the API itself down, a 500) says nothing about the gate.
  return { access: "unknown", openReports: null };
}

/**
 * The window the gate probe reads.
 *
 * It must stay equal to `DEFAULT_REPORT_LOOKBACK` in
 * `features/dynamics/console-format.ts` — that is what makes the probe, the
 * overview tile and the queue's own first render *one* cached query instead of
 * three. The constant cannot simply be imported: `console-format.ts` is
 * exercised by `node --test` through type-stripping, so it may hold type-only
 * imports and nothing else. `tests/nav-registry.test.mjs` asserts the two
 * literals agree, so a change to either is caught rather than silently costing
 * an extra round-trip per page.
 */
const GATE_PROBE_LOOKBACK: FoConsoleReportLookback = "7d";

/**
 * The HTTP status a thrown `ApiError` carries, or `null`.
 *
 * The same three lines as `apiErrorStatus` in the Dynamics feature. Duplicated
 * rather than imported because the import would run the wrong way — `lib/api`
 * must not depend on a feature — and lifting it here would drop it out of the
 * type-stripped test that covers it today.
 */
function thrownStatus(error: unknown): number | null {
  if (error && typeof error === "object" && "status" in error) {
    const status = (error as { status: unknown }).status;
    return typeof status === "number" ? status : null;
  }
  return null;
}

/**
 * One report in full, with its matched records. Opening it is an investigator
 * action (D7): the call carries the session's reason and writes an audit row,
 * so it fires only with both an id and a reason, never polls, never retries.
 */
export function useFoConsoleReport(id: number | null, reason: string | null) {
  return useQuery({
    queryKey: [...FO_CONSOLE_REPORT_KEY, id ?? 0, reason ?? ""],
    queryFn: async () => {
      const { data } = await getFoConsoleReport({
        path: { report_id: id as number },
        query: { reason: reason as string },
      });
      logger.debug("[fo-console] report received", {
        matches: data?.matches.length ?? 0,
        matchState: data?.match_state,
        status: data?.status,
      });
      return data;
    },
    enabled: id != null && Boolean(reason),
    gcTime: IDENTITY_GC_MS,
    staleTime: 60_000,
    retry: false,
  });
}

/** Move a report between `new`, `triaged` and `closed`; the list and the detail re-read. */
export function useUpdateFoConsoleReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (variables: { id: number; status: FoConsoleReportStatus }) => {
      const { data } = await updateFoConsoleReport({
        path: { report_id: variables.id },
        body: { status: variables.status },
      });
      return data;
    },
    onSuccess: (_data, variables) => {
      logger.debug("[fo-console] report status updated", { status: variables.status });
      void queryClient.invalidateQueries({ queryKey: FO_CONSOLE_REPORTS_KEY });
      void queryClient.invalidateQueries({ queryKey: FO_CONSOLE_REPORT_KEY });
    },
  });
}

/**
 * Browser RUM across the apps the extension reports for (`/fo/console/experience`):
 * per-app aggregates, TTFB by hour, the worst contexts (k-folded) and the idle
 * probes. Counts and medians only, no poster identity — persistable, polls on
 * the payload's TTL like the slow summary.
 */
export function useFoConsoleExperience(lookback: FoConsoleLookback) {
  return useQuery({
    queryKey: [...FO_CONSOLE_EXPERIENCE_KEY, lookback],
    queryFn: async () => {
      const { data } = await getFoConsoleExperience({ query: { lookback } });
      logger.debug("[fo-console] experience received", {
        accessState: data?.access_state,
        apps: data?.apps.length ?? 0,
        hours: data?.by_hour.length ?? 0,
      });
      return data;
    },
    refetchInterval: (query) => intervalFromTtl(query.state.data),
    placeholderData: (previous) => previous,
  });
}
